import { isAdmin, isPartner } from '../../../helpers/appType';
import {
  ADMIN_BRAND_DETAILS,
  ADMIN_BRAND_MANAGE_PROVIDERS,
  ADMIN_CHAT,
  ADMIN_CLIENT_PAYMENTS,
  ADMIN_CLOSING_DAYS,
  ADMIN_DASHBOARD,
  ADMIN_DELIVERIES_DETAILS,
  ADMIN_LOCATIONS,
  ADMIN_LOCATIONS_DETAILS,
  ADMIN_LOCATIONS_V_DRIVE,
  ADMIN_ORDER_DETAILS,
  ADMIN_RECEIVABLES_LOCATION,
  ADMIN_SUPPORT_TICKETS,
  ADMIN_SYSTEM_PROBLEMS,
  ADMIN_TRANSACTIONS,
  ADMIN_VERIFICATIONS,
  ADMIN_VERIFICATIONS_AGENT,
  ADMIN_VERIFICATIONS_BRAND,
  ADMIN_VERIFICATIONS_RESTAURANT,
  ADMIN_VERIFICATIONS_V_DRIVE,
  ADMIN_WORKING_HOURS,
  CHAT_CONVERSATION_PARAM,
  EDIT_LOCATION_PARAM,
  PARTNER_CHAT,
  PARTNER_REFER_A_RESTAURANT,
  PARTNER_TRANSACTIONS,
  PARTNER_V_DRIVE,
  SUPPORT_TICKET_PARAM,
  SYSTEM_PROBLEM_PARAM,
} from '../../../routes/adminConstants';
import type { NotificationEntity } from '../../../types/notification.types';
import { UserRole } from '../../../types/user.types';

/**
 * Where a click on a notification goes.
 *
 * - `to`: a route in this app.
 * - `uiState`: tabs that live in `useUIState` rather than the URL - set just
 *   before navigating, so the page opens on them (see `useNotificationActions`).
 * - `state`: router state the page reads once on arrival (the merchant's
 *   vDrive page opens its card form from it).
 * - `external`: `to` is a URL outside the app, opened in a new tab.
 */
export type NotificationTarget = {
  to: string;
  uiState?: { verificationsTab?: number; workingHoursTab?: number };
  state?: unknown;
  external?: boolean;
};

/** The vDrive tab of Verifications - `verificationsTab` in `useUIState`. */
const VERIFICATIONS_TAB_VDRIVE = 3;
/** The two tabs of the Working Hours page - `workingHoursTab`. */
const WORKING_HOURS_TAB = 1;
const HOLIDAY_HOURS_TAB = 2;

/**
 * An id from `data`, falling back to `itemId`.
 *
 * `data` is null on rows written before the bell existed and on some older
 * types, and for those `itemId` is the only id there is - which is why the
 * fallback is per type below, never a blanket one: `itemId` is a different
 * entity for every type, and for some it is 0 (a daily summary).
 */
const idFrom = (
  notification: NotificationEntity,
  key: string,
  { orItemId = false } = {}
): number | null => {
  const value = notification.data?.[key];
  if (typeof value === 'number' || typeof value === 'string') {
    const id = Number(value);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return orItemId && notification.itemId ? notification.itemId : null;
};

const withId = (path: string, id: number | null) =>
  id ? path.replace(':id', String(id)) : null;

const route = (to: string | null): NotificationTarget | null =>
  to ? { to } : null;

/**
 * A page with the location (and brand) picker pre-filled. Working Hours and
 * Closing Days both read `locationIds` / `brandIds` - comma separated, first
 * one used.
 */
const withLocationFilter = (
  path: string,
  locationId: number | null,
  brandId: number | null
) => {
  const params = new URLSearchParams();
  if (locationId) params.set('locationIds', String(locationId));
  if (brandId) params.set('brandIds', String(brandId));
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

const orderDetails = (n: NotificationEntity) =>
  withId(ADMIN_ORDER_DETAILS, idFrom(n, 'orderId', { orItemId: true }));

/**
 * Hours that someone changed. `change` names the screen; for the two hours
 * tabs the page is one and the tab is UI state. `brandId` is there when the
 * change was to a brand's hours rather than the whole location's.
 */
const hoursTarget = (n: NotificationEntity): NotificationTarget => {
  const locationId = idFrom(n, 'locationId');
  const brandId = idFrom(n, 'brandId');
  const change = n.data?.change;

  if (change === 'closingDays') {
    return { to: withLocationFilter(ADMIN_CLOSING_DAYS, locationId, brandId) };
  }
  return {
    to: withLocationFilter(ADMIN_WORKING_HOURS, locationId, brandId),
    uiState: {
      workingHoursTab:
        change === 'holidayHours' ? HOLIDAY_HOURS_TAB : WORKING_HOURS_TAB,
    },
  };
};

const adminTarget = (n: NotificationEntity): NotificationTarget | null => {
  const locationId = idFrom(n, 'locationId');
  const brandId = idFrom(n, 'brandId', { orItemId: true });

  switch (n.type) {
    case 'locationVerifyReady':
      return route(
        withId(
          ADMIN_VERIFICATIONS_RESTAURANT,
          idFrom(n, 'locationId', { orItemId: true })
        )
      );
    case 'agentVerifyReady':
      // The agent verification page is keyed on the agent's user id, which is
      // what an agent's id is.
      return route(
        withId(
          ADMIN_VERIFICATIONS_AGENT,
          idFrom(n, 'agentId', { orItemId: true })
        )
      );
    case 'vDriveVerifyReady': {
      /* The request page is keyed on the location (the first one, when the
         owner asked for several). Rows from before `locationId` was sent
         name only the owner, so those open the vDrive tab of the list. */
      const requestPage = withId(ADMIN_VERIFICATIONS_V_DRIVE, locationId);
      return requestPage
        ? { to: requestPage }
        : {
            to: ADMIN_VERIFICATIONS,
            uiState: { verificationsTab: VERIFICATIONS_TAB_VDRIVE },
          };
    }
    case 'brandRequestCreated':
      return route(withId(ADMIN_VERIFICATIONS_BRAND, brandId));
    case 'integrationAccessGranted':
      // An incomplete location has no page of its own yet; the restaurant
      // queue on Verifications is where it turns up once it is submitted.
      return route(
        withId(ADMIN_LOCATIONS_DETAILS, locationId) ?? ADMIN_VERIFICATIONS
      );
    case 'systemProblem': {
      const problemId = idFrom(n, 'systemProblemId', { orItemId: true });
      return route(
        problemId
          ? `${ADMIN_SYSTEM_PROBLEMS}?${SYSTEM_PROBLEM_PARAM}=${problemId}`
          : ADMIN_SYSTEM_PROBLEMS
      );
    }
    case 'providersRemoved':
    case 'foodhubConnectionRemoved':
      if (brandId && locationId) {
        return route(
          ADMIN_BRAND_MANAGE_PROVIDERS.replace(':id', String(brandId)).replace(
            ':locId',
            String(locationId)
          )
        );
      }
      return route(withId(ADMIN_BRAND_DETAILS, brandId));
    case 'vDriveDeliveryCancelled':
    case 'vDriveReassignFailed':
      // The delivery, not the order: the delivery page is keyed on the
      // OrderVDrive id, and it is the delivery that failed.
      return route(
        withId(
          ADMIN_DELIVERIES_DETAILS,
          idFrom(n, 'orderVDriveId', { orItemId: true })
        ) ?? withId(ADMIN_ORDER_DETAILS, idFrom(n, 'orderId'))
      );
    case 'vDriveAddressChanged':
      // `itemId` is the order here, so the delivery comes from `data` only.
      return route(
        withId(ADMIN_DELIVERIES_DETAILS, idFrom(n, 'orderVDriveId')) ??
          orderDetails(n)
      );
    case 'autoCompleteFailed':
      return route(orderDetails(n));
    case 'supportTicketOpened': {
      const ticketId = idFrom(n, 'ticketId', { orItemId: true });
      return route(
        ticketId
          ? `${ADMIN_SUPPORT_TICKETS}?${SUPPORT_TICKET_PARAM}=${ticketId}`
          : ADMIN_SUPPORT_TICKETS
      );
    }
    case 'chatActionFailed': {
      // Support threads are listed under the Merchants scope, which is the
      // default, so the id alone opens it.
      const conversationId = idFrom(n, 'conversationId');
      return route(
        conversationId
          ? `${ADMIN_CHAT}?${CHAT_CONVERSATION_PARAM}=${conversationId}`
          : ADMIN_CHAT
      );
    }
    case 'closingDayPauseFailed':
      // `itemId` is the location or the closing day, depending on where it
      // was sent from - so only `data` is trusted for the location.
      return route(
        withLocationFilter(ADMIN_CLOSING_DAYS, locationId, idFrom(n, 'brandId'))
      );
    case 'hoursChanged':
      return hoursTarget(n);
    case 'achMandateInvalid':
      return route(
        withId(
          ADMIN_LOCATIONS_DETAILS,
          idFrom(n, 'locationId', { orItemId: true })
        )
      );
    case 'saasChargeFailed': {
      const details = withId(
        ADMIN_LOCATIONS_DETAILS,
        idFrom(n, 'locationId', { orItemId: true })
      );
      /* GoCardless refused the location's ACH customer, which is the ACH
         Customer ID on Edit Location - so that modal opens. `unknown` is ours
         to look into, and old rows say nothing, so those get the page. */
      const achProblem =
        n.data?.reason === 'achCustomerInvalid' ||
        n.data?.reason === 'gocardlessError';
      return route(
        details && achProblem ? `${details}?${EDIT_LOCATION_PARAM}=1` : details
      );
    }
    case 'vDrivePaused':
      // Paused for what it owes - the location's vDrive page shows the
      // balance and the pause.
      return route(
        withId(
          ADMIN_LOCATIONS_V_DRIVE,
          idFrom(n, 'locationId', { orItemId: true })
        )
      );
    case 'payoutSkipped': {
      // A payout is a withdrawal from that user's vGrubs wallet. Nothing
      // links to a single transaction, so the tab it is listed under.
      const userId = idFrom(n, 'userId');
      return route(
        userId ? `${withId(ADMIN_TRANSACTIONS, userId)}?type=Withdrawals` : null
      );
    }
    case 'clientPaymentFailed':
    case 'achRetryResult': {
      /* The location's Outstanding Balance lists its payments with Retry on
         the failed ones. Without a location, the list of every payment. */
      const outstanding = locationId
        ? ADMIN_RECEIVABLES_LOCATION.replace(':locationId', String(locationId))
        : null;
      return route(
        outstanding ??
          (n.type === 'clientPaymentFailed'
            ? `${ADMIN_CLIENT_PAYMENTS}?status=failed`
            : ADMIN_CLIENT_PAYMENTS)
      );
    }
    case 'paymentReportFailed':
      // A daily sum across providers (`itemId` is 0). There is no screen for
      // the report imports themselves; the Finances tab is where the
      // report-based numbers they feed are read.
      return route(`${ADMIN_DASHBOARD}?tab=finances`);
    default:
      return null;
  }
};

/** Only a web link, so a bad value can never become a `javascript:` URL. */
const externalLink = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};

const partnerTarget = (
  n: NotificationEntity,
  role: UserRole | undefined
): NotificationTarget | null => {
  const locationDetails = withId(
    ADMIN_LOCATIONS_DETAILS,
    idFrom(n, 'locationId', { orItemId: true })
  );
  /* The wallet's history is the owner's alone - a manager is sent nowhere
     rather than to a page the router would bounce them off. */
  const wallet = role === UserRole.partner ? PARTNER_TRANSACTIONS : null;

  switch (n.type) {
    case 'locationVerified':
    case 'locationRejected':
    case 'vRevenueEnabled':
    case 'saasPackageAssigned':
    case 'outstandingBalance':
    case 'outstandingBalancePause':
    case 'achMandateInvalid':
      // The location page is where its billing notices are - the services,
      // the balance owed and the plan paying it off.
      return route(locationDetails);
    case 'brandRequest':
      /* The merchant app has no page for a brand on its own; it is listed on
         its location. Old rows name only the brand. */
      return route(
        withId(ADMIN_LOCATIONS_DETAILS, idFrom(n, 'locationId')) ??
          ADMIN_LOCATIONS
      );
    case 'recoveryStatusChanged':
      /* Evidence is sent through the support chat - the text says so - so
         that is where the tap goes. The other outcomes are about the order. */
      return route(
        n.data?.status === 'evidenceNeeded' ? PARTNER_CHAT : orderDetails(n)
      );
    case 'orderRecovered':
      return route(orderDetails(n));
    case 'balanceAdjusted':
      // Either balance can be adjusted; the vDrive one lives on its own page.
      return route(n.data?.wallet === 'vdrive' ? PARTNER_V_DRIVE : wallet);
    case 'vDrivePaymentReminder': {
      /* A backup card the merchant adds themselves: the vDrive page opens its
         card form for this location. A bank is added through support, so a
         reminder about the bank alone opens the chat. */
      const locationId = idFrom(n, 'locationId', { orItemId: true });
      if (n.data?.missingBackupCard === false && n.data?.missingBank) {
        return route(PARTNER_CHAT);
      }
      return locationId
        ? {
            to: PARTNER_V_DRIVE,
            state: {
              addCardFor: { id: locationId },
              addCardSlot: 'backup',
            },
          }
        : route(PARTNER_V_DRIVE);
    }
    case 'locationDeduction':
    case 'transactionProcessed':
    case 'transactionFailed':
    case 'transactionCancelled':
      return route(wallet);
    case 'bankRemoved':
      // The bank account sits on the Dashboard, beside the wallet balance.
      return route(ADMIN_DASHBOARD);
    case 'deviceShipped': {
      // The carrier's tracking page; the location when there is no link.
      const tracking = externalLink(n.data?.trackingLink);
      return tracking
        ? { to: tracking, external: true }
        : route(withId(ADMIN_LOCATIONS_DETAILS, idFrom(n, 'locationId')));
    }
    case 'holidayClosingReminder':
      return route(ADMIN_CLOSING_DAYS);
    case 'vDriveVerified':
    case 'vDriveRejected':
    case 'vDriveCardFailed':
    case 'vDriveBackupCard':
    case 'vDriveOverdraft':
    case 'vDriveCardSunset':
      return route(PARTNER_V_DRIVE);
    case 'referralSignedUp':
    case 'referralEarned':
      return route(PARTNER_REFER_A_RESTAURANT);
    default:
      return null;
  }
};

/**
 * Where this notification leads in this app, or `null` for none.
 *
 * `null` is a normal answer: a type this build does not know yet - they are
 * added on the backend without an API change - is still listed and can still
 * be marked read, it just goes nowhere.
 */
export const notificationTarget = (
  notification: NotificationEntity,
  role: UserRole | undefined
): NotificationTarget | null => {
  if (isAdmin) return adminTarget(notification);
  if (isPartner) return partnerTarget(notification, role);
  return null;
};
